import { useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from "recharts";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const TASKS = [
  { name: "Funding", start: 0, end: 3 },
  { name: "Pre-prod", start: 2, end: 5 },
  { name: "Production", start: 4, end: 7 },
  { name: "Post-prod", start: 6, end: 9 },
];

const MONTHS = [
  { value: 0, label: "Jan" },
  { value: 1, label: "Feb" },
  { value: 2, label: "Mar" },
  { value: 3, label: "Apr" },
  { value: 4, label: "May" },
  { value: 5, label: "Jun" },
  { value: 6, label: "Jul" },
  { value: 7, label: "Aug" },
  { value: 8, label: "Sep" },
  { value: 9, label: "Oct" },
];

const COLORS = ["#e43c3c", "#6dbf6e", "#7c6ddb", "#ea843b"];

export default function AnimatedGanttChart() {
  const containerRef = useRef();
  const maxDurationsRef = useRef(TASKS.map(() => 0));
  const [data, setData] = useState(
    TASKS.map(task => ({
      ...task,
      startOffset: task.start,
      duration: 0,
      label: `${task.start} → ${task.end}`
    }))
  );

  const maxEnd = Math.max(...TASKS.map(t => t.end));

  useEffect(() => {
    const progressRef = { value: 0 };

    const updateData = () => {
      const progress = progressRef.value;

      const newData = TASKS.map((task, i) => {
        let visibleDuration = (task.end - task.start) * progress;
        if (visibleDuration > task.end - task.start) visibleDuration = task.end - task.start;

        // Forward-only logic
        if (visibleDuration > maxDurationsRef.current[i]) {
          maxDurationsRef.current[i] = visibleDuration;
        }
        visibleDuration = maxDurationsRef.current[i];

        return {
          ...task,
          startOffset: task.start,
          duration: visibleDuration,
          label: `${task.start} → ${task.end}`
        };
      });

      setData(newData);
    };

    gsap.to(progressRef, {
      value: 1,
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%",
        end: "bottom bottom",
        scrub: 0.5
      },
      onUpdate: updateData
    });

    return () => ScrollTrigger.killAll();
  }, []);

  return (
    <div ref={containerRef} style={{ width: "100%", height: 400, }}>
<ResponsiveContainer>
  <BarChart
    layout="vertical"
    data={data}
    margin={{ top: 50, right: 50, left: 150, bottom: 20 }} // top increased for months
    barCategoryGap="30%"
  >
<XAxis
  type="number"
  domain={[0, maxEnd]}
  ticks={MONTHS.map(m => m.value)}
  allowDecimals={false}
  tick={({ x, y, payload }) => {
    const month = MONTHS.find(m => m.value === payload.value);
    return month ? (
      <foreignObject x={x - 20} y={y} width={60} height={40}>
        <h2 style={{ color: "#fff", fontWeight: "bold", margin: 0, fontSize: "13px", textAlign: "center" }}>
          {month.label}
        </h2>
      </foreignObject>
    ) : null;
  }}
/>
    <YAxis
  dataKey="name"
  type="category"
  tick={({ x, y, payload }) => (
    <foreignObject x={x - 180} y={y - 12} width={145} height={35}>
      <h2 style={{ color: "#292727", margin: 0, fontSize: "10px" }}>
        {payload.value}
      </h2>
    </foreignObject>
  )}
/>
    <Tooltip
    cursor={{ fill: 'rgba(243, 174, 61, 0.1)' }}
      formatter={(value, name, props) => {
        const task = props.payload;
        return [`Start: ${task.start}, End: ${task.end}`, "Task"];
      }}
       contentStyle={{
              backgroundColor: 'rgba(20,19,19,0.9)',
              border: '1px solid rgba(243,174,61,0.4)',
              borderRadius: '8px',
              color: '#fff',
            }}
    />
    <Bar dataKey="startOffset" stackId="a" fill="transparent" isAnimationActive={false}/>
    <Bar dataKey="duration" stackId="a" isAnimationActive={false}>
      {data.map((entry, index) => (
        <Cell key={index} fill={COLORS[index % COLORS.length]}
        />
      ))}
      <LabelList
        dataKey="label"
        position="insideRight"
        style={{ fill: "#fff", fontWeight: "bold" }}
      />
    </Bar>
  </BarChart>
</ResponsiveContainer>
    </div>
  );
}